import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { Home, Search, PlusSquare, Compass, Menu, X, MessageCircle, Bell, Coins, User, Users, LogOut, Shield, Trophy, Wallet as WalletIcon, BarChart3, Crown, ChevronDown, Radio, LogIn, Sparkles, GraduationCap } from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useWS } from "@/context/WSContext";
import { api, picUrl } from "@/lib/api";

const itemCls = ({ isActive }) =>
  `flex flex-col items-center justify-center gap-0.5 px-3 py-2 transition-colors ${
    isActive ? "text-[#39FF14]" : "text-white/60"
  }`;

// Collapsible group heading for the mobile menu. Keeps the growing menu
// scannable — sections default to open on first mount, but the user's
// preference is remembered in localStorage so their layout sticks.
function NavSection({ id, label, defaultOpen = true, children }) {
  const storeKey = `neosync_nav_sec_${id}`;
  const [open, setOpen] = useState(() => {
    try {
      const stored = localStorage.getItem(storeKey);
      if (stored === "open") return true;
      if (stored === "closed") return false;
    } catch { /* incognito / disabled — fall through */ }
    return defaultOpen;
  });
  const toggle = () => {
    setOpen((prev) => {
      try { localStorage.setItem(storeKey, prev ? "closed" : "open"); } catch { /* ignore */ }
      return !prev;
    });
  };
  return (
    <div className="border-t border-white/5">
      <button
        type="button"
        onClick={toggle}
        data-testid={`mobile-nav-group-${id}`}
        aria-expanded={open}
        className="w-full flex items-center gap-2 px-5 pt-4 pb-2 text-left text-white/40 hover:text-white/70"
      >
        <span className="flex-1 font-mono text-[10px] uppercase tracking-[0.25em]">
          {label}
        </span>
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-0" : "-rotate-90"}`}
          strokeWidth={2}
        />
      </button>
      {open && <div>{children}</div>}
    </div>
  );
}

export default function BottomNav() {
  const { user, logout } = useAuth();
  const { subscribe } = useWS();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [notifUnread, setNotifUnread] = useState(0);
  const [coins, setCoins] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [feedSpin, setFeedSpin] = useState(false);

  useEffect(() => {
    if (!user) {
      setUnread(0);
      setNotifUnread(0);
      setCoins(0);
      setIsAdmin(false);
      return;
    }
    const loadDms = () =>
      api.get("/messages/unread-count").then((r) => setUnread(r.data.unread || 0)).catch(() => {});
    const loadNotifs = () =>
      api.get("/notifications/unread-count").then((r) => setNotifUnread(r.data.count || 0)).catch(() => {});
    const loadCoins = () =>
      api.get("/me/earnings").then((r) => setCoins(r.data.coins_balance || 0)).catch(() => {});
    // Cheap admin check: try admin endpoint, 200 → admin, 403 → nope
    api.get("/admin/payouts?status=pending").then(() => setIsAdmin(true)).catch(() => setIsAdmin(false));
    loadDms();
    loadNotifs();
    loadCoins();
    return subscribe((evt) => {
      if (evt.type === "message:new" || evt.type === "conversation:read") loadDms();
      if (evt.type === "notification_new") setNotifUnread((n) => n + 1);
      if (evt.type === "tip_received" && evt.amount_cents) {
        setCoins((c) => c + Math.floor(evt.amount_cents / 2));
      }
    });
  }, [user, subscribe]);

  const badgeTotal = unread + notifUnread;

  return (
    <>
      <nav
        data-testid="bottom-nav"
        className="md:hidden fixed bottom-0 inset-x-0 h-16 bg-[#050505]/95 backdrop-blur-xl border-t border-white/10 flex items-center justify-around z-40"
      >
        <NavLink
          to="/"
          end
          className={itemCls}
          data-testid="bottomnav-feed"
          onClick={(e) => {
            // If the user is already on the Feed route, tapping FEED again
            // should reshuffle the algorithm (fresh videos, fresh order)
            // rather than being a no-op. Dispatch a custom event that the
            // Feed page listens for and calls its handleRefresh().
            if (location.pathname === "/") {
              e.preventDefault();
              window.dispatchEvent(new CustomEvent("feed:refresh"));
              // Kick off a neon spin on the Home icon so the user gets
              // instant visual confirmation the feed is reshuffling.
              setFeedSpin(true);
              window.setTimeout(() => setFeedSpin(false), 700);
            }
          }}
        >
          <Home
            className={`h-5 w-5 transition-transform duration-700 ${feedSpin ? "rotate-[360deg] text-[#39FF14] drop-shadow-[0_0_6px_rgba(57,255,20,0.9)]" : ""}`}
            strokeWidth={1.5}
            data-testid="bottomnav-feed-icon"
          />
          <span className="text-[10px] font-mono uppercase">Feed</span>
        </NavLink>
        <NavLink to="/discover" className={itemCls} data-testid="bottomnav-discover">
          <Compass className="h-5 w-5" strokeWidth={1.5} />
          <span className="text-[10px] font-mono uppercase">Trends</span>
        </NavLink>
        <NavLink to="/live" className={itemCls} data-testid="bottomnav-live">
          <div className="relative">
            <Radio className="h-5 w-5 text-[#FF007F]" strokeWidth={1.5} />
            <span className="absolute -top-1 -right-1 h-1.5 w-1.5 rounded-full bg-[#FF007F] animate-pulse" />
          </div>
          <span className="text-[10px] font-mono uppercase text-[#FF007F]">Live</span>
        </NavLink>
        <NavLink to="/upload" className={itemCls} data-testid="bottomnav-upload">
          <div className="h-9 w-9 bg-[#39FF14] flex items-center justify-center">
            <PlusSquare className="h-5 w-5 text-black" strokeWidth={2} />
          </div>
        </NavLink>
        <NavLink to="/search" className={itemCls} data-testid="bottomnav-search">
          <Search className="h-5 w-5" strokeWidth={1.5} />
          <span className="text-[10px] font-mono uppercase">Search</span>
        </NavLink>
        <button
          data-testid="bottomnav-menu"
          onClick={() => {
            // Logged-out visitors: send straight to /login so they don't
            // have to hunt through the menu to figure out where to sign up.
            if (!user) {
              navigate("/login");
              return;
            }
            setOpen(true);
          }}
          className={`flex flex-col items-center justify-center gap-0.5 px-3 py-2 hover:text-white transition-colors relative ${
            !user
              ? "animate-signin-halo text-[#39FF14]"
              : "text-white/60"
          }`}
        >
          {!user ? (
            <LogIn className="h-5 w-5 animate-signin-nudge" strokeWidth={2} />
          ) : (
            <Menu className="h-5 w-5" strokeWidth={1.5} />
          )}
          <span
            className={`text-[10px] font-mono uppercase font-bold ${
              !user ? "animate-signin-nudge tracking-wider" : ""
            }`}
          >
            {!user ? "Sign in" : "Menu"}
          </span>
          {badgeTotal > 0 && (
            <span
              data-testid="bottomnav-menu-dot"
              className="absolute top-1.5 right-2 h-2 w-2 bg-[#FF007F] shadow-[0_0_8px_rgba(255,0,127,0.8)]"
            />
          )}
        </button>
      </nav>

      {open && (
        <div
          data-testid="mobile-menu-sheet"
          className="md:hidden fixed inset-0 z-50 flex flex-col justify-end"
        >
          <button
            aria-label="Close menu"
            onClick={() => setOpen(false)}
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          />
          <div className="relative bg-[#0A0A0C] border-t border-[#39FF14]/40 shadow-[0_-20px_50px_rgba(57,255,20,0.15)] pb-6">
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#39FF14]">
                // menu
              </p>
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                className="h-8 w-8 flex items-center justify-center text-white/60 hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {user && (
              <Link
                to={`/u/${user.username}`}
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 px-5 py-3 border-b border-white/10 hover:bg-white/5"
              >
                {user.picture ? (
                  <img
                    src={picUrl(user.picture)}
                    alt=""
                    className="h-10 w-10 object-cover border border-white/10 flex-shrink-0"
                  />
                ) : (
                  <div className="h-10 w-10 bg-[#39FF14]/20 flex items-center justify-center text-[#39FF14] font-bold flex-shrink-0">
                    {user.name?.[0]?.toUpperCase() || "?"}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm font-semibold truncate">
                    {user.name}
                  </p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                    @{user.username}
                  </p>
                </div>
              </Link>
            )}

            <div className="py-2">
              {user && (
                <Link
                  to="/messages"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-messages"
                  className="flex items-center gap-3 px-5 py-3 hover:bg-white/5 text-white/80"
                >
                  <MessageCircle className="h-5 w-5 text-[#00FFFF]" strokeWidth={1.6} />
                  <span className="flex-1 text-sm">Inbox</span>
                  {unread > 0 && (
                    <span
                      data-testid="mobile-inbox-badge"
                      className="bg-[#FF007F] text-black text-[10px] font-bold px-2 py-0.5"
                    >
                      {unread}
                    </span>
                  )}
                </Link>
              )}
              {user && (
                <Link
                  to="/notifications"
                  onClick={() => {
                    setOpen(false);
                    setNotifUnread(0);
                  }}
                  data-testid="mobile-nav-notifications"
                  className="flex items-center gap-3 px-5 py-3 hover:bg-white/5 text-white/80"
                >
                  <Bell className="h-5 w-5 text-[#39FF14]" strokeWidth={1.6} />
                  <span className="flex-1 text-sm">Notifications</span>
                  {notifUnread > 0 && (
                    <span
                      data-testid="mobile-notif-badge"
                      className="bg-[#39FF14] text-black text-[10px] font-bold px-2 py-0.5 shadow-[0_0_10px_rgba(57,255,20,0.5)]"
                    >
                      {notifUnread}
                    </span>
                  )}
                </Link>
              )}
              {/* ---- MONEY ---- */}
              {user && (
                <NavSection id="money" label="Money">
                  <Link
                    to="/wallet"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-nav-wallet"
                    className="flex items-center gap-3 px-5 py-3 hover:bg-white/5 text-white/80"
                  >
                    <WalletIcon className="h-5 w-5 text-[#39FF14]" strokeWidth={1.6} />
                    <span className="flex-1 text-sm">Wallet</span>
                    {(user?.wallet_balance_cents || 0) > 0 && (
                      <span
                        data-testid="mobile-wallet-badge"
                        className="bg-[#39FF14] text-black text-[10px] font-bold px-2 py-0.5 shadow-[0_0_10px_rgba(57,255,20,0.5)]"
                      >
                        ${((user.wallet_balance_cents || 0) / 100).toFixed(0)}
                      </span>
                    )}
                  </Link>
                  <Link
                    to="/earnings"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-nav-earnings"
                    className="flex items-center gap-3 px-5 py-3 hover:bg-white/5 text-white/80"
                  >
                    <Coins className="h-5 w-5 text-[#FF007F]" strokeWidth={1.6} />
                    <span className="flex-1 text-sm">Earnings</span>
                    {coins > 0 && (
                      <span
                        data-testid="mobile-coins-badge"
                        className="bg-[#FF007F] text-white text-[10px] font-bold px-2 py-0.5 shadow-[0_0_10px_rgba(255,0,127,0.5)]"
                      >
                        {coins > 999 ? `${(coins / 1000).toFixed(1)}k` : coins}
                      </span>
                    )}
                  </Link>
                  <Link
                    to="/shop-analytics"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-nav-shop-stats"
                    className="flex items-center gap-3 px-5 py-3 hover:bg-white/5 text-white/80"
                  >
                    <BarChart3 className="h-5 w-5 text-[#39FF14]" strokeWidth={1.6} />
                    <span className="flex-1 text-sm">Shop &amp; Shares</span>
                  </Link>
                  <Link
                    to="/my-signups"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-nav-my-signups"
                    className="flex items-center gap-3 px-5 py-3 hover:bg-white/5 text-white/80"
                  >
                    <Users className="h-5 w-5 text-[#39FF14]" strokeWidth={1.6} />
                    <span className="flex-1 text-sm">My Signups</span>
                    <span className="font-mono text-[9px] uppercase tracking-widest text-[#39FF14] border border-[#39FF14]/50 px-1.5 py-0.5">
                      10%
                    </span>
                  </Link>
                </NavSection>
              )}

              {/* ---- CREATOR & YOU ---- */}
              {user && (
                <NavSection id="creator" label="Creator & You">
                  <Link
                    to="/pro"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-nav-pro"
                    className="flex items-center gap-3 px-5 py-3 hover:bg-white/5 text-white/80"
                  >
                    <Crown className="h-5 w-5 text-[#B026FF]" strokeWidth={1.6} />
                    <span className="flex-1 text-sm">Pro</span>
                    {user?.is_pro ? (
                      <span
                        data-testid="mobile-pro-badge"
                        className="bg-[#B026FF] text-white text-[10px] font-bold px-2 py-0.5 uppercase tracking-widest shadow-[0_0_10px_rgba(176,38,255,0.5)]"
                      >
                        pro
                      </span>
                    ) : (
                      <span
                        data-testid="mobile-pro-upsell-chip"
                        className="font-mono text-[9px] uppercase tracking-widest text-[#B026FF] border border-[#B026FF]/50 px-1.5 py-0.5"
                      >
                        new
                      </span>
                    )}
                  </Link>
                  <Link
                    to="/my-boosts"
                    onClick={() => setOpen(false)}
                    data-testid="mobile-nav-my-boosts"
                    className="flex items-center gap-3 px-5 py-3 hover:bg-white/5 text-white/80"
                  >
                    <Trophy className="h-5 w-5 text-[#FFB800]" strokeWidth={1.6} />
                    <span className="flex-1 text-sm">Trophy Shelf</span>
                  </Link>
                  <Link
                    to={`/u/${user.username}`}
                    onClick={() => setOpen(false)}
                    data-testid="mobile-nav-profile"
                    className="flex items-center gap-3 px-5 py-3 hover:bg-white/5 text-white/80"
                  >
                    <User className="h-5 w-5 text-white/60" strokeWidth={1.6} />
                    <span className="flex-1 text-sm">Profile</span>
                  </Link>
                </NavSection>
              )}
              {user && isAdmin && (
                <Link
                  to={`/admin/user/${user.username}/shares`}
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-admin-shares"
                  className="flex items-center gap-3 px-5 py-3 hover:bg-white/5 text-white/80 border-t border-white/5"
                >
                  <BarChart3 className="h-5 w-5 text-[#00FFFF]" strokeWidth={1.6} />
                  <span className="flex-1 text-sm">Admin · Share Activity</span>
                </Link>
              )}
              {user && isAdmin && (
                <Link
                  to={`/admin/user/${user.username}/signups`}
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-admin-signups"
                  className="flex items-center gap-3 px-5 py-3 hover:bg-white/5 text-white/80"
                >
                  <Users className="h-5 w-5 text-[#FFC300]" strokeWidth={1.6} />
                  <span className="flex-1 text-sm">Admin · Referrals</span>
                </Link>
              )}
              {user && isAdmin && (
                <Link
                  to="/admin/payouts"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-admin"
                  className="flex items-center gap-3 px-5 py-3 hover:bg-white/5 text-white/80 border-t border-white/5"
                >
                  <Shield className="h-5 w-5 text-[#FF007F]" strokeWidth={1.6} />
                  <span className="flex-1 text-sm">Admin · Payouts</span>
                </Link>
              )}
              {user && isAdmin && (
                <Link
                  to="/admin/gifts"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-admin-gifts"
                  className="flex items-center gap-3 px-5 py-3 hover:bg-white/5 text-white/80"
                >
                  <Sparkles className="h-5 w-5 text-[#FFC300]" strokeWidth={1.6} />
                  <span className="flex-1 text-sm">Admin · Boost Animations</span>
                </Link>
              )}
              {user && isAdmin && (
                <Link
                  to="/admin/analytics"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-admin-analytics"
                  className="flex items-center gap-3 px-5 py-3 hover:bg-white/5 text-white/80"
                >
                  <BarChart3 className="h-5 w-5 text-[#FF007F]" strokeWidth={1.6} />
                  <span className="flex-1 text-sm">Admin · Analytics</span>
                </Link>
              )}
              {user && isAdmin && (
                <Link
                  to="/admin/guide-agent"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-admin-guide-agent"
                  className="flex items-center gap-3 px-5 py-3 hover:bg-white/5 text-white/80"
                >
                  <GraduationCap className="h-5 w-5 text-[#39FF14]" strokeWidth={1.6} />
                  <span className="flex-1 text-sm">Admin · Guide Agent</span>
                </Link>
              )}
              {user && (
                <button
                  data-testid="mobile-nav-signout"
                  onClick={() => {
                    setOpen(false);
                    logout();
                    navigate("/");
                  }}
                  className="w-full flex items-center gap-3 px-5 py-3 hover:bg-white/5 text-white/60 border-t border-white/10 mt-2"
                >
                  <LogOut className="h-5 w-5" strokeWidth={1.6} />
                  <span className="flex-1 text-sm text-left">Sign out</span>
                </button>
              )}
              {!user && (
                <Link
                  to="/login"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-login"
                  className="flex items-center gap-3 px-5 py-3 hover:bg-white/5 text-[#39FF14]"
                >
                  <User className="h-5 w-5" strokeWidth={1.6} />
                  <span className="flex-1 text-sm">Sign in</span>
                </Link>
              )}
              <div className="flex items-center justify-center gap-3 pt-4 pb-1 font-mono text-[9px] uppercase tracking-widest text-white/30">
                <Link
                  to="/terms"
                  data-testid="mobile-nav-terms"
                  onClick={() => setOpen(false)}
                  className="hover:text-white/60"
                >
                  terms
                </Link>
                <span>·</span>
                <Link
                  to="/privacy"
                  data-testid="mobile-nav-privacy"
                  onClick={() => setOpen(false)}
                  className="hover:text-white/60"
                >
                  privacy
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
